import { useState } from "react";
import { Check, X, Edit2, FileText, ChevronDown, ChevronRight } from "lucide-react";

/**
 * ConfirmationCard — In-chat card for confirming/rejecting extracted data.
 *
 * Rendered inside the chat message list when type === "confirmation_card".
 */

const SECTION_LABELS = {
  therapy: "Therapy",
  indication: "Indication",
  clinical_endpoints: "Clinical Endpoints",
  dose_config: "Dose Configuration",
  dose_scenarios: "Dose Scenarios",
  competitive_landscape: "Competitive Landscape",
  commercial_assumptions: "Commercial Assumptions",
};

const CONFIDENCE_COLORS = {
  high: "text-emerald-400",
  medium: "text-amber-400",
  low: "text-slate-400",
};

// Flatten a nested object into readable key-value pairs
function flattenForDisplay(obj, prefix = "") {
  const rows = [];
  if (!obj || typeof obj !== "object") return rows;

  for (const [key, val] of Object.entries(obj)) {
    const label = prefix ? `${prefix}.${key}` : key;
    const displayLabel = label.replace(/_/g, " ").replace(/\./g, " > ");

    if (val === null || val === undefined || val === "") continue;

    if (Array.isArray(val)) {
      if (val.length === 0) continue;
      if (typeof val[0] === "object") {
        // Array of objects — show count + first item preview
        const names = val
          .map((v) => v.name || v.id || JSON.stringify(v))
          .slice(0, 3)
          .join(", ");
        const suffix = val.length > 3 ? ` (+${val.length - 3} more)` : "";
        rows.push({ key: displayLabel, value: `${val.length} items: ${names}${suffix}` });
      } else {
        rows.push({ key: displayLabel, value: val.join(", ") });
      }
    } else if (typeof val === "object") {
      rows.push(...flattenForDisplay(val, label));
    } else {
      rows.push({ key: displayLabel, value: String(val) });
    }
  }
  return rows;
}

export default function ConfirmationCard({
  sectionKey,
  extractedData,
  confidence = "medium",
  sources = [],
  onConfirm,
  onEdit,
  onReject,
  status = "pending", // "pending" | "confirmed" | "rejected" | "modified"
}) {
  const [expanded, setExpanded] = useState(true);
  const title = SECTION_LABELS[sectionKey] || sectionKey.replace(/_/g, " ");
  const rows = flattenForDisplay(extractedData);
  const isActioned = status !== "pending";

  const statusBadge = {
    confirmed: { label: "Confirmed", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
    rejected: { label: "Rejected", color: "text-red-400 bg-red-500/10 border-red-500/30" },
    modified: { label: "Modified", color: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  };

  return (
    <div className={`border rounded-lg overflow-hidden my-1 ${isActioned ? "border-slate-700/50 opacity-75" : "border-cyan-500/30 bg-slate-800/60"}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/80">
        <button
          onClick={() => setExpanded((p) => !p)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
        >
          {expanded ? (
            <ChevronDown size={11} className="text-slate-500 flex-shrink-0" />
          ) : (
            <ChevronRight size={11} className="text-slate-500 flex-shrink-0" />
          )}
          <span className="text-[11px] font-medium text-cyan-300">{title}</span>
          <span className={`text-[9px] ${CONFIDENCE_COLORS[confidence]}`}>
            {confidence}
          </span>
        </button>
        {isActioned && statusBadge[status] && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${statusBadge[status].color}`}>
            {statusBadge[status].label}
          </span>
        )}
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-3 py-2">
          {/* Data rows */}
          {rows.length > 0 ? (
            <div className="flex flex-col gap-1 mb-2">
              {rows.slice(0, 8).map((r, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[9px] text-slate-500 min-w-[6rem] flex-shrink-0 capitalize">
                    {r.key}
                  </span>
                  <span className="text-[10px] text-slate-300 break-words">{r.value}</span>
                </div>
              ))}
              {rows.length > 8 && (
                <span className="text-[9px] text-slate-600 italic">
                  +{rows.length - 8} more fields
                </span>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-slate-600 italic mb-2">No data extracted for this section.</p>
          )}

          {/* Sources */}
          {sources.length > 0 && (
            <div className="flex items-start gap-1 mb-2">
              <FileText size={9} className="text-slate-600 mt-0.5 flex-shrink-0" />
              <span className="text-[9px] text-slate-600">
                {sources.slice(0, 2).join(" | ")}
                {sources.length > 2 && ` (+${sources.length - 2})`}
              </span>
            </div>
          )}

          {/* Actions */}
          {!isActioned && (
            <div className="flex items-center gap-1.5 pt-1 border-t border-slate-700/50">
              <button
                onClick={() => onConfirm?.(sectionKey, extractedData)}
                className="flex items-center gap-1 text-[10px] font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded px-2 py-1 transition-colors"
              >
                <Check size={10} /> Accept
              </button>
              <button
                onClick={() => onEdit?.(sectionKey)}
                className="flex items-center gap-1 text-[10px] font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded px-2 py-1 transition-colors"
              >
                <Edit2 size={10} /> Edit
              </button>
              <button
                onClick={() => onReject?.(sectionKey)}
                className="flex items-center gap-1 text-[10px] font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded px-2 py-1 transition-colors"
              >
                <X size={10} /> Reject
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
